import { message } from 'antd';
// eslint-disable-next-line no-restricted-imports -- the interceptor is the one module that calls axios
import axios, { AxiosResponse, AxiosError } from 'axios';
import { jwtDecode as jwt_decode } from 'jwt-decode';
import { getSessionSignal, getTokenGeneration } from './sessionAbortController';
import { getActiveToken, isPreviewSession } from '@/common/helper/tokenStorage';
import { clearAllStorageAndState } from '@/common/helper/storageCleanup';
import { OperationType } from '@/common/enum';
export { OperationType };

// Track shown error messages to prevent duplicates
const shownErrorMessages = new Set<string>();

export const clearErrorTracking = (): void => {
  shownErrorMessages.clear();
};

/**
 * Resolves an AbortSignal combining the global session signal with an optional caller-provided signal.
 */
export const resolveAbortSignal = (
  callerSignal?: AbortSignal | null
): AbortSignal => {
  const sessionSignal = getSessionSignal();
  if (!callerSignal) {
    return sessionSignal;
  }
  if (
    typeof AbortSignal !== 'undefined' &&
    'any' in AbortSignal &&
    typeof AbortSignal.any === 'function'
  ) {
    return AbortSignal.any([sessionSignal, callerSignal]);
  }
  const controller = new AbortController();
  const onAbort = () => {
    controller.abort();
    sessionSignal.removeEventListener('abort', onAbort);
    callerSignal.removeEventListener('abort', onAbort);
  };
  if (sessionSignal.aborted || callerSignal.aborted) {
    controller.abort();
  } else {
    sessionSignal.addEventListener('abort', onAbort, { once: true });
    callerSignal.addEventListener('abort', onAbort, { once: true });
  }
  return controller.signal;
};

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 300, // milliseconds
  delayMultiplier: [1, 2.67, 5], // ~300ms, ~800ms, ~1500ms
  jitterRange: 0.2, // ±20% randomness
};

/**
 * Adds jitter to delay to prevent thundering herd
 */
const addJitter = (delay: number): number => {
  const jitter = delay * RETRY_CONFIG.jitterRange;
  return delay + (Math.random() * 2 - 1) * jitter;
};

/**
 * Delays execution for the specified time with jitter
 */
const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, addJitter(ms)));
};

/**
 * Checks if the error is retryable (network errors or 5xx gateway errors)
 */
const isRetryableError = (error: AxiosError): boolean => {
  // Never retry cancelled or aborted requests
  const errorMessageLower = (error.message || '').toLowerCase();
  if (
    error.code === 'ERR_CANCELED' ||
    errorMessageLower.includes('canceled') ||
    errorMessageLower.includes('cancelled') ||
    axios.isCancel(error) ||
    error.name === 'CanceledError' ||
    error.name === 'AbortError'
  ) {
    return false;
  }

  // No response = network error (DNS, TLS, timeout, connection reset)
  if (!error.response) {
    return true;
  }

  // Retry on gateway errors
  const retryableStatusCodes = [502, 503, 504];
  return retryableStatusCodes.includes(error.response.status);
};

// TypeScript interfaces
interface JWTTokenPayload {
  time_zone_enable?: boolean;
  user_time_zone?: string;
  user_id?: number;
  is_preview?: boolean;
  [key: string]: unknown;
}

/**
 * Axios base segment before each `urlPath`.
 * Empty `REACT_APP_BASE_URL` uses Vite's `/api` proxy — **must be root-relative `/api`**,
 * not `api`; otherwise URLs from routes like `/dev/...` resolve to `/dev/api/...`
 * (404) instead of `http(s)://<host>/api/...`.
 */
function resolveApiBaseSegment(): string {
  const raw = process.env.REACT_APP_BASE_URL;
  const prefix = raw?.trim();
  if (prefix == null || prefix === '') {
    return '/api';
  }
  // Root-relative base (e.g. "/") — keep a single leading slash before "api"
  if (prefix.startsWith('/')) {
    const base = prefix.replace(/\/+$/, '');
    return base === '' ? '/api' : `${base}/api`;
  }
  // Absolute URL — require exactly one slash before "api"
  const normalizedPrefix = prefix.replace(/\/+$/, '');
  return `${normalizedPrefix}/api`;
}

export const baseUrl: string = resolveApiBaseSegment();

export const guardPreviewMutations = (
  operationType: OperationType = OperationType.Write
): boolean => {
  if (
    operationType === OperationType.Read ||
    operationType === OperationType.NonBlockedWrite
  ) {
    return false;
  }
  return isPreviewSession();
};

/**
 * Returns the user's timezone from the JWT token only when the user has
 * turned the timezone preference on (`time_zone_enable`); otherwise falls back
 * to the browser timezone.
 *
 * @returns User's timezone string
 */
export const getUserTimeZone = (): string => {
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const userToken = getActiveToken();
  if (!userToken) return browserTimeZone;
  const decodedToken = jwt_decode<JWTTokenPayload>(userToken);
  return decodedToken?.time_zone_enable && decodedToken?.user_time_zone
    ? decodedToken.user_time_zone
    : browserTimeZone;
};

export const getToken = (_token?: string | null): string | null => {
  return getActiveToken();
};

/** Bulk Step-1 → Step-2 column validation only (not other APIs). */
const BULK_VALIDATE_V2_PATH = '/shipment/bulk-validate/v2/';

function isBulkValidateV2Request(error: AxiosError): boolean {
  const url = error.config?.url ?? '';
  return url.includes(BULK_VALIDATE_V2_PATH);
}

/**
 * Read user-facing error text from ZoomLogi envelope or DRF-shaped bodies.
 */
export function extractResponseErrorMessage(data: unknown): string | undefined {
  if (data == null || typeof data !== 'object') {
    return undefined;
  }
  const root = data as Record<string, unknown>;

  // ZoomLogi `return_class`: { data: { message: string, ... } }
  const inner = root.data;
  if (inner != null && typeof inner === 'object') {
    const msg = (inner as Record<string, unknown>).message;
    if (typeof msg === 'string' && msg.trim()) {
      return msg.trim();
    }
  }

  // DRF: { detail: string | string[] }
  if (typeof root.detail === 'string' && root.detail.trim()) {
    return root.detail.trim();
  }
  if (Array.isArray(root.detail) && root.detail.length > 0) {
    const parts = root.detail.filter((x) => typeof x === 'string') as string[];
    if (parts.length > 0) {
      return parts.join('; ');
    }
  }

  if (typeof root.message === 'string' && root.message.trim()) {
    return root.message.trim();
  }

  return undefined;
}

/** Returns whether a failed request got an HTTP 4xx response, which a retry gets again. */
export function isRefusedRequest(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response === undefined) {
    return false;
  }
  const { status } = error.response;
  return status >= 400 && status < 500;
}

/**
 * Handles HTTP errors with appropriate user messaging and authentication cleanup
 *
 * @param error - Axios error object
 * @param isRetrying - Whether this error occurred during a retry attempt (suppresses toasts)
 * @returns Rejected promise to propagate the error
 */
const handleHttpError = (
  error: AxiosError,
  isRetrying = false
): Promise<never> => {
  const errorMessageLower = (error.message || '').toLowerCase();
  if (
    error.code === 'ERR_CANCELED' ||
    errorMessageLower.includes('canceled') ||
    errorMessageLower.includes('cancelled') ||
    axios.isCancel(error) ||
    error.name === 'CanceledError' ||
    error.name === 'AbortError'
  ) {
    return Promise.reject(error);
  }

  let errorMessage = '';

  if (error.response) {
    const statusCode = error.response.status;
    const fromBody = extractResponseErrorMessage(error.response.data);
    const preferBulkValidateBodyMessage =
      (statusCode === 400 || statusCode === 422) &&
      isBulkValidateV2Request(error);

    errorMessage = preferBulkValidateBodyMessage
      ? fromBody || error.response.statusText || 'An error occurred'
      : fromBody ||
        error.response.statusText ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error.response.data as any)?.data?.message ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error.response.data as any)?.message?.messageTypeId ||
        'An error occurred';

    // 401 Unauthorized: Invalid or expired credentials -> clear session and redirect to login
    if (statusCode === 401) {
      // Ignore stale 401s from previous session generations
      const requestTokenGen = (
        error.config as { _tokenGeneration?: number } | undefined
      )?._tokenGeneration;
      const currentGen = getTokenGeneration();
      if (typeof requestTokenGen === 'number' && requestTokenGen < currentGen) {
        return Promise.reject(error);
      }

      message.error(errorMessage);
      const _ = clearAllStorageAndState({
        broadcast: true,
        navigateToLogin: true,
      });
      return Promise.reject(error);
    }

    // Suppress toasts during retries for retryable errors
    if (isRetrying && isRetryableError(error)) {
      return Promise.reject(error);
    }

    // Lane planner owns its error UX, including 403 when no vendor is enabled
    // for the company, so suppress global toasts before the generic 403
    // branch. 401 session handling above still applies to Zoomlogi auth. Vendor
    // connections (Settings) keep the global toasts.
    const requestUrl = String(error.config?.url || '');
    if (
      statusCode >= 400 &&
      statusCode < 500 &&
      requestUrl.includes('/lane-planner/') &&
      !requestUrl.includes('/vendor-connections/')
    ) {
      return Promise.reject(error);
    }

    // 403 Forbidden: Permission denied or read-only preview block -> show toast without logging out
    if (statusCode === 403) {
      message.error('You do not have permission to perform this action.');
      return Promise.reject(error);
    }

    // Show toasts for non-retryable errors or final retry failure
    if (statusCode === 500) {
      if (!shownErrorMessages.has(errorMessage)) {
        shownErrorMessages.add(errorMessage);
        message.error(errorMessage);
      }
    } else if (statusCode >= 400 && statusCode < 500) {
      message.error(errorMessage);
    }
  } else if (error.request) {
    // Network error - only show after all retries exhausted
    if (isRetrying) {
      return Promise.reject(error);
    }

    // After all retries failed, show offline message
    if (!shownErrorMessages.has('network_error')) {
      shownErrorMessages.add('network_error');
      //message.error(errorMessage);
    }
  } else {
    errorMessage = error.message || 'An unexpected error occurred';
    if (!isRetrying && !shownErrorMessages.has(errorMessage)) {
      shownErrorMessages.add(errorMessage);
      message.error(errorMessage);
    }
  }

  return Promise.reject(error);
};

/**
 * Retries a request function with exponential backoff
 * Suppresses error toasts during retries, only showing them on final failure
 */
const withRetry = async <T>(
  requestFn: () => Promise<T>,
  errorHandler: (error: AxiosError, isRetrying: boolean) => Promise<never>,
  attemptNumber = 0
): Promise<T> => {
  try {
    const result = await requestFn();
    // Clear network error tracking on success
    shownErrorMessages.delete('network_error');
    return result;
  } catch (error) {
    const axiosError = error as AxiosError;

    // Check if this error is retryable and we haven't exceeded max retries
    const shouldRetry =
      isRetryableError(axiosError) && attemptNumber < RETRY_CONFIG.maxRetries;

    if (shouldRetry) {
      // Calculate delay with exponential backoff
      const delayMs =
        RETRY_CONFIG.baseDelay * RETRY_CONFIG.delayMultiplier[attemptNumber];

      // Wait before retrying
      await delay(delayMs);

      // Retry with incremented attempt number
      return withRetry(requestFn, errorHandler, attemptNumber + 1);
    } else {
      // Final attempt failed or non-retryable error - show error toast
      return errorHandler(axiosError, false);
    }
  }
};

/**
 * HTTP request interceptor with authentication and timezone handling
 *
 * Provides standardized HTTP methods with automatic JWT token processing,
 * timezone extraction from user tokens, and centralized error handling.
 * All methods return axios responses and handle authentication headers automatically.
 */
const interceptor = {
  axiosPost: (
    method: string,
    urlPath: string,
    data: unknown | undefined,
    token?: string,
    signal?: AbortSignal | null,
    operationType: OperationType = OperationType.Write,
    // Background calls the user never triggered directly (e.g. the activity
    // beacon) opt in: failures reject without a toast and without the
    // 401 logout -- the user's own requests handle an expired session.
    // 'keep-auth' only drops the toast: a 401 still clears the session.
    silentErrors: boolean | 'keep-auth' = false,
    extraHeaders?: Record<string, string>
  ): Promise<AxiosResponse> => {
    const isBlocked = guardPreviewMutations(operationType);
    if (isBlocked) {
      if (operationType !== OperationType.SilentWrite) {
        message.error('Read-only preview: write operations are blocked.');
      }

      return Promise.reject(
        new Error('Read-only preview: write operations are blocked.')
      );
    }
    const finalToken = getToken(token);
    const userTimeZone = getUserTimeZone();
    return axios({
      method: method,
      url: `${baseUrl}${urlPath}`,
      data: data,
      headers: {
        Authorization: `Bearer ${finalToken}`,
        'Content-Type': 'application/json',
        timezone: userTimeZone,
        ...extraHeaders,
      },
      signal: resolveAbortSignal(signal),
      ...({ _tokenGeneration: getTokenGeneration() } as Record<
        string,
        unknown
      >),
    })
      .then((res) => {
        return res;
      })
      .catch((error) => {
        return silentErrors === true ||
          (silentErrors === 'keep-auth' && error.response?.status !== 401)
          ? Promise.reject(error)
          : handleHttpError(error);
      });
  },

  axiosWithoutHaeders: (
    method: string,
    urlPath: string,
    data?: unknown,
    signal?: AbortSignal | null,
    silentErrors = false
  ): Promise<AxiosResponse> => {
    return axios({
      method: method,
      url: `${baseUrl}${urlPath}`,
      data: data,
      headers: {
        Authorization: null,
        'Content-Type': 'application/json',
        timeZone:
          localStorage.getItem('user_time_zone') ||
          Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      signal: resolveAbortSignal(signal),
      ...({ _tokenGeneration: getTokenGeneration() } as Record<
        string,
        unknown
      >),
    })
      .then((res) => {
        return res;
      })
      .catch((error) => {
        return silentErrors && error.response?.status !== 401
          ? Promise.reject(error)
          : handleHttpError(error);
      });
  },

  axiosPostForUpload: (
    method: string,
    urlPath: string,
    data: unknown | undefined,
    token?: string,
    signal?: AbortSignal | null,
    // The caller shows the failure itself (inline), so no toast.
    silentErrors = false
  ): Promise<AxiosResponse> => {
    const isBlocked = guardPreviewMutations();
    if (isBlocked) {
      message.error('Read-only preview: write operations are blocked.');
      return Promise.reject(
        new Error('Read-only preview: write operations are blocked.')
      );
    }
    const finalToken = getToken(token);
    const userTimeZone = getUserTimeZone();
    return axios({
      method: method,
      url: `${baseUrl}${urlPath}`,
      data: data,
      headers: {
        Authorization: `Bearer ${finalToken}`,
        'Content-Type': 'multipart/form-data',
        timezone: userTimeZone,
      },
      signal: resolveAbortSignal(signal),
      ...({ _tokenGeneration: getTokenGeneration() } as Record<
        string,
        unknown
      >),
    })
      .then((res) => {
        return res;
      })
      .catch((error) => {
        return silentErrors && error.response?.status !== 401
          ? Promise.reject(error)
          : handleHttpError(error);
      });
  },

  axiosLogin: (
    method: string,
    urlPath: string,
    data?: unknown,
    signal?: AbortSignal | null
  ): Promise<AxiosResponse> => {
    return axios({
      method: method,
      url: `${baseUrl}${urlPath}`,
      data: data,
      signal: resolveAbortSignal(signal),
      ...({ _tokenGeneration: getTokenGeneration() } as Record<
        string,
        unknown
      >),
    })
      .then((res) => {
        return res;
      })
      .catch((error) => {
        return handleHttpError(error);
      });
  },

  axiosPut: (
    method: string,
    urlPath: string,
    data: unknown | undefined,
    token?: string,
    fetchOptions?: RequestInit,
    signal?: AbortSignal | null,
    operationType: OperationType = OperationType.Write
  ): Promise<AxiosResponse> => {
    const isBlocked = guardPreviewMutations(operationType);
    if (isBlocked) {
      message.error('Read-only preview: write operations are blocked.');
      return Promise.reject(
        new Error('Read-only preview: write operations are blocked.')
      );
    }
    const userTimeZone = getUserTimeZone();
    const finalToken = getToken(token);
    // When fetchOptions are provided (e.g. keepalive for unload-time saves), use
    // the fetch adapter so those options pass through. The default XHR adapter
    // can't express keepalive.
    const request = axios({
      method: method,
      url: `${baseUrl}${urlPath}`,
      data: data,
      headers: {
        Authorization: `Bearer ${finalToken}`,
        'Content-Type': 'application/json',
        timezone: userTimeZone,
      },
      signal: resolveAbortSignal(signal),
      ...({ _tokenGeneration: getTokenGeneration() } as Record<
        string,
        unknown
      >),
      ...(fetchOptions ? { adapter: 'fetch', fetchOptions } : {}),
    });
    // Keepalive PUTs fire during page unload — skip the global error handler so
    // we don't pop a toast or redirect-to-/login on an unmounting page. Other
    // fetchOptions (cache, mode, etc.) still go through the normal handler.
    if (fetchOptions?.keepalive) {
      return request;
    }
    return request
      .then((res) => {
        return res;
      })
      .catch((error) => {
        return handleHttpError(error);
      });
  },

  axiosGet: (
    method: string,
    urlPath: string,
    token?: string,
    data?: unknown,
    signal?: AbortSignal | null,
    silentErrors = false,
    timeoutMs?: number
  ): Promise<AxiosResponse> => {
    const userTimeZone = getUserTimeZone();
    const finalToken = getToken(token);

    // Wrap GET requests with automatic retry logic
    return withRetry(
      () =>
        axios({
          method: method,
          url: `${baseUrl}${urlPath}`,
          data: data,
          headers: {
            Authorization: `Bearer ${finalToken}`,
            timezone: userTimeZone,
          },
          // A per-attempt budget: axios rejects with ECONNABORTED (no response),
          // which `withRetry` treats as a network error and retries.
          ...(timeoutMs ? { timeout: timeoutMs } : {}),
          signal: resolveAbortSignal(signal), // Support request cancellation and session abort
          ...({ _tokenGeneration: getTokenGeneration() } as Record<
            string,
            unknown
          >),
        }),
      (error: AxiosError) =>
        silentErrors && error.response?.status !== 401
          ? Promise.reject(error)
          : handleHttpError(error)
    );
  },

  axiosDelete: (
    method: string,
    urlPath: string,
    data: unknown | undefined,
    token?: string,
    signal?: AbortSignal | null,
    operationType: OperationType = OperationType.Write
  ): Promise<AxiosResponse> => {
    const isBlocked = guardPreviewMutations(operationType);
    if (isBlocked) {
      message.error('Read-only preview: write operations are blocked.');
      return Promise.reject(
        new Error('Read-only preview: write operations are blocked.')
      );
    }
    const userTimeZone = getUserTimeZone();
    const finalToken = getToken(token);
    return axios({
      method: method,
      url: `${baseUrl}${urlPath}`,
      data: data,
      headers: {
        Authorization: `Bearer ${finalToken}`,
        timezone: userTimeZone,
      },
      signal: resolveAbortSignal(signal),
      ...({ _tokenGeneration: getTokenGeneration() } as Record<
        string,
        unknown
      >),
    })
      .then((res) => {
        return res;
      })
      .catch((error) => {
        return handleHttpError(error);
      });
  },

  axiosGetDownload: (
    method: string,
    urlPath: string,
    token?: string,
    data?: unknown,
    signal?: AbortSignal | null
  ): Promise<AxiosResponse> => {
    const userTimeZone = getUserTimeZone();
    const finalToken = getToken(token);

    // Wrap GET download requests with automatic retry logic
    return withRetry(
      () =>
        axios({
          method: method,
          url: `${baseUrl}${urlPath}`,
          data: data,
          headers: {
            Authorization: `Bearer ${finalToken}`,
            timezone: userTimeZone,
          },
          responseType: 'blob',
          signal: resolveAbortSignal(signal),
          ...({ _tokenGeneration: getTokenGeneration() } as Record<
            string,
            unknown
          >),
        }),
      handleHttpError
    );
  },
};

export default interceptor;
